import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import { supabase } from "../../../lib/supabase";
import { useApp } from "../../../contexts/AppContext";
import { useCategories } from "../../../hooks/useCategories";

function getSuggestedPrice(duration: number): { price: number; label: string } | null {
  if (!duration || duration <= 0) return null;
  if (duration <= 8) return { price: 0, label: "無料サンプル" };
  if (duration <= 20) return { price: 1480, label: "お試し講座" };
  if (duration <= 35) return { price: 2980, label: "入門講座" };
  if (duration <= 50) return { price: 4980, label: "基礎講座" };
  if (duration <= 70) return { price: 6980, label: "実践講座" };
  if (duration <= 90) return { price: 8980, label: "中上級講座" };
  if (duration <= 120) return { price: 11800, label: "本格講座" };
  return { price: 16800, label: "専門講座・まとめ講座" };
}

export default function SellVideoPage() {
  const navigate = useNavigate();
  const { currentUser } = useApp();
  const { categories, refetch } = useCategories();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ title: "", category: "", difficulty: "beginner", target: "", duration: "", price: "", isFree: false, description: "", curriculum: "", visibility: "public" });
  const [saved, setSaved] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [addingCat, setAddingCat] = useState(false);

  const handleAddCategory = async () => {
    if (!newCatName.trim() || !currentUser?.id) return;
    setAddingCat(true);
    const maxOrder = categories.length > 0 ? Math.max(...categories.map((c) => c.sort_order ?? 0)) : 0;
    const { error: err } = await supabase.from("categories").insert({
      name: newCatName.trim(),
      icon: "ri-folder-line",
      visible: true,
      sort_order: maxOrder + 1,
      status: "approved",
      created_by: currentUser.id,
    });
    setAddingCat(false);
    if (!err) {
      setForm(f => ({ ...f, category: newCatName.trim() }));
      setNewCatName("");
      setShowNewCat(false);
      if (refetch) refetch();
    }
  };

  const duration = parseInt(form.duration) || 0;
  const suggested = getSuggestedPrice(duration);
  const price = parseInt(form.price) || 0;
  const isPriceLow = suggested && price > 0 && price < suggested.price * 0.5;
  const isPriceHigh = suggested && price > 0 && price > suggested.price * 2;

  const handleSave = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };
  const handleComplete = async () => {
    if (step < 4) { setStep(s => s + 1); return; }
    if (!currentUser?.id) { setError("ログインが必要です"); return; }
    setSubmitting(true);
    const { error: err } = await supabase.from("products").insert({
      provider_id: currentUser.id,
      title: form.title || "無題の講座",
      type: "video",
      price: parseInt(form.price) || 0,
      description: form.description,
      category: form.category,
      status: "public",
      total_minutes: parseInt(form.duration) || 0,
      difficulty: form.difficulty,
      target_audience: form.target,
      is_free_sample: form.isFree,
      curriculum: form.curriculum,
      visibility: form.visibility,
      sales_count: 0,
    });
    setSubmitting(false);
    if (err) { setError(err.message); return; }
    setCompleted(true);
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 flex items-center justify-center bg-teal rounded-full mx-auto mb-6 animate-bounce">
            <i className="ri-checkbox-circle-fill text-white text-5xl" />
          </div>
          <h2 className="font-serif text-deep-green text-3xl mb-3">出品完了！</h2>
          <p className="text-charcoal/60 font-sans mb-6">あなたの講座が公開されました。</p>
          <div className="flex gap-3 justify-center">
            <Link to="/mypage/provider" className="bg-deep-green text-white px-6 py-3 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">マイページへ</Link>
            <Link to="/sell/video" onClick={() => window.location.reload()} className="border border-deep-green text-deep-green px-6 py-3 rounded-full text-sm font-sans cursor-pointer hover:bg-ivory transition-colors whitespace-nowrap">もう1本出品</Link>
          </div>
        </div>
      </div>
    );
  }

  const steps = ["基本情報", "動画・時間", "価格設定", "詳細・公開"];

  return (
    <div className="min-h-screen bg-ivory">
      <Navbar />
      <main className="pt-20">
        <section className="bg-deep-green py-10 px-4">
          <div className="max-w-screen-xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Link to="/sell" className="text-white/50 text-sm font-sans cursor-pointer hover:text-white">出品トップ</Link>
              <i className="ri-arrow-right-s-line text-white/30" />
              <p className="text-white text-sm font-sans">動画講座を出品</p>
            </div>
            <h1 className="font-serif text-white text-3xl font-light">動画講座を出品</h1>
            <p className="text-white/50 text-sm font-sans mt-1">残り動画投稿枠：1本（フリープラン）</p>
          </div>
        </section>

        {/* Step Indicator */}
        <section className="bg-white border-b border-stone-100 px-4 py-4 sticky top-16 z-20">
          <div className="max-w-2xl mx-auto flex items-center gap-2">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center flex-1">
                <button onClick={() => i + 1 < step && setStep(i + 1)} className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-sans flex-shrink-0 transition-all cursor-pointer ${i + 1 < step ? "bg-teal text-white" : i + 1 === step ? "bg-deep-green text-white" : "bg-stone-200 text-charcoal/40"}`}>
                  {i + 1 < step ? <i className="ri-check-line" /> : i + 1}
                </button>
                <p className={`text-xs font-sans ml-1.5 whitespace-nowrap hidden sm:block ${i + 1 === step ? "text-deep-green font-medium" : "text-charcoal/40"}`}>{s}</p>
                {i < steps.length - 1 && <div className={`flex-1 h-px mx-2 ${i + 1 < step ? "bg-teal" : "bg-stone-200"}`} />}
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 py-8">
          <div className="max-w-2xl mx-auto">
            {step === 1 && (
              <div className="space-y-5 bg-white rounded-2xl p-6 border border-stone-100">
                <h2 className="font-serif text-deep-green text-lg">基本情報</h2>
                <div>
                  <label className="block text-xs font-sans text-charcoal/60 mb-1.5">講座タイトル <span className="text-red-400">*</span></label>
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal transition-colors" placeholder="例：プロのしみ抜き完全攻略講座" />
                </div>
                <div>
                  <label className="block text-xs font-sans text-charcoal/60 mb-1.5">カテゴリ <span className="text-red-400">*</span></label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal transition-colors bg-white cursor-pointer">
                    <option value="">選択してください</option>
                    {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                  {/* Inline category add */}
                  {!showNewCat ? (
                    <button
                      type="button"
                      onClick={() => setShowNewCat(true)}
                      className="mt-1.5 text-xs text-teal font-sans flex items-center gap-1 cursor-pointer hover:text-deep-green transition-colors"
                    >
                      <i className="ri-add-circle-line" />
                      カテゴリを新規追加する
                    </button>
                  ) : (
                    <div className="mt-2 flex items-center gap-2 bg-teal/5 border border-teal/20 rounded-xl p-3">
                      <input
                        value={newCatName}
                        onChange={e => setNewCatName(e.target.value)}
                        placeholder="新しいカテゴリ名を入力"
                        maxLength={40}
                        className="flex-1 bg-transparent border-none outline-none text-sm font-sans text-charcoal placeholder-charcoal/30"
                        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleAddCategory(); } }}
                      />
                      <button
                        type="button"
                        onClick={handleAddCategory}
                        disabled={addingCat || !newCatName.trim()}
                        className="px-3 py-1.5 bg-teal text-white text-xs rounded-lg font-sans cursor-pointer disabled:opacity-60 whitespace-nowrap hover:bg-deep-green transition-colors"
                      >
                        {addingCat ? <i className="ri-loader-4-line animate-spin" /> : "追加"}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowNewCat(false); setNewCatName(""); }}
                        className="w-6 h-6 flex items-center justify-center text-charcoal/30 hover:text-charcoal cursor-pointer"
                      >
                        <i className="ri-close-line text-sm" />
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-sans text-charcoal/60 mb-1.5">難易度</label>
                  <div className="flex gap-2">
                    {[{ v: "beginner", l: "初心者向け" }, { v: "intermediate", l: "中級者向け" }, { v: "advanced", l: "上級者向け" }].map((d) => (
                      <button key={d.v} onClick={() => setForm(f => ({ ...f, difficulty: d.v }))} className={`flex-1 py-2 rounded-xl text-xs font-sans border transition-colors cursor-pointer whitespace-nowrap ${form.difficulty === d.v ? "border-teal bg-teal/10 text-teal" : "border-stone-200 text-charcoal/60 hover:border-teal/40"}`}>{d.l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-sans text-charcoal/60 mb-1.5">対象者</label>
                  <input value={form.target} onChange={e => setForm(f => ({ ...f, target: e.target.value }))} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal transition-colors" placeholder="例：クリーニング業に従事している方、着物愛好家" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <div className="bg-white rounded-2xl p-6 border border-stone-100">
                  <h2 className="font-serif text-deep-green text-lg mb-4">動画・時間設定</h2>
                  <div className="border-2 border-dashed border-stone-200 rounded-2xl p-8 text-center mb-5 hover:border-teal transition-colors cursor-pointer">
                    <i className="ri-upload-cloud-2-line text-4xl text-charcoal/30 block mb-3" />
                    <p className="text-sm font-sans text-charcoal/50">動画ファイルをアップロード</p>
                    <p className="text-xs text-charcoal/30 font-sans mt-1">MP4 / MOV 対応 · 最大2GB</p>
                  </div>
                  <div>
                    <label className="block text-xs font-sans text-charcoal/60 mb-1.5">動画の長さ（分） <span className="text-red-400">*</span></label>
                    <input type="number" min="1" max="300" value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal transition-colors" placeholder="例：45" />
                    <p className="text-xs text-charcoal/40 font-sans mt-1">動画の正確な時間（分）を入力すると推奨価格が表示されます</p>
                  </div>
                  {suggested && (
                    <div className="mt-4 bg-teal/5 border border-teal/20 rounded-xl p-4">
                      <p className="text-xs text-teal font-sans font-medium mb-1">{form.duration}分の動画 → 種別：{suggested.label}</p>
                      <p className="text-sm font-sans text-charcoal">推奨価格は <strong className="text-teal">¥{suggested.price === 0 ? "0（無料サンプル）" : suggested.price.toLocaleString()}</strong> です</p>
                      {suggested.price === 0 && <p className="text-xs text-charcoal/50 font-sans mt-1">3〜8分の動画は無料サンプルとして設定することを推奨します。購入への入口として効果的です。</p>}
                    </div>
                  )}
                </div>
                <div className="bg-white rounded-2xl p-6 border border-stone-100">
                  <h3 className="font-sans text-sm font-medium text-charcoal mb-3">無料サンプル設定</h3>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setForm(f => ({ ...f, isFree: !f.isFree }))} className={`w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.isFree ? "bg-teal" : "bg-stone-200"}`}>
                      <div className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform mx-0.5 ${form.isFree ? "translate-x-6" : "translate-x-0"}`} />
                    </button>
                    <div>
                      <p className="text-sm font-sans text-charcoal">この動画を無料サンプルに設定する</p>
                      <p className="text-xs text-charcoal/40 font-sans">未購入ユーザーも視聴可能になります（3〜8分を推奨）</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="bg-white rounded-2xl p-6 border border-stone-100 space-y-5">
                <h2 className="font-serif text-deep-green text-lg">価格設定</h2>
                {suggested && (
                  <div className="bg-stone-50 rounded-xl p-4">
                    <p className="text-xs text-charcoal/50 font-sans mb-2">{duration}分の動画に対する推奨価格</p>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 text-center p-3 bg-teal/10 rounded-xl">
                        <p className="text-xs text-charcoal/40 font-sans mb-1">推奨価格</p>
                        <p className="font-serif text-teal text-xl">¥{suggested.price === 0 ? "0" : suggested.price.toLocaleString()}</p>
                        <p className="text-xs text-charcoal/40 font-sans">{suggested.label}</p>
                      </div>
                      <i className="ri-arrow-right-line text-charcoal/30 text-xl flex-shrink-0" />
                      <div className="flex-1 text-center p-3 bg-deep-green/10 rounded-xl">
                        <p className="text-xs text-charcoal/40 font-sans mb-1">あなたの設定価格</p>
                        <p className="font-serif text-deep-green text-xl">¥{price > 0 ? price.toLocaleString() : "—"}</p>
                        {price > 0 && suggested.price > 0 && (
                          <p className={`text-xs font-sans ${price === suggested.price ? "text-teal" : "text-charcoal/40"}`}>
                            {price === suggested.price ? "推奨価格と同じ" : price < suggested.price ? `推奨より¥${(suggested.price - price).toLocaleString()}安` : `推奨より¥${(price - suggested.price).toLocaleString()}高`}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-sans text-charcoal/60 mb-1.5">販売価格（円）<span className="text-red-400">*</span></label>
                  <input type="number" min="0" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal transition-colors" placeholder={suggested ? `推奨：¥${suggested.price.toLocaleString()}` : "例：4,980"} />
                </div>
                {isPriceLow && <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700 font-sans"><i className="ri-information-line mr-1" />設定価格が推奨価格の半額以下です。専門技術の価値を適切に反映した価格設定をお勧めします。</div>}
                {isPriceHigh && <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-xs text-orange-700 font-sans"><i className="ri-information-line mr-1" />設定価格が推奨価格の2倍以上です。購入を検討する方にとって高すぎる可能性があります。</div>}
              </div>
            )}

            {step === 4 && (
              <div className="space-y-5">
                <div className="bg-white rounded-2xl p-6 border border-stone-100 space-y-4">
                  <h2 className="font-serif text-deep-green text-lg">詳細情報</h2>
                  <div>
                    <label className="block text-xs font-sans text-charcoal/60 mb-1.5">講座の概要</label>
                    <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={4} maxLength={500} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal transition-colors resize-none" placeholder="この講座で学べる内容、受講後にできるようになること..." />
                    <p className="text-right text-xs text-charcoal/30 font-sans mt-1">{form.description.length}/500</p>
                  </div>
                  <div>
                    <label className="block text-xs font-sans text-charcoal/60 mb-1.5">カリキュラム（章立て）</label>
                    <textarea value={form.curriculum} onChange={e => setForm(f => ({ ...f, curriculum: e.target.value }))} rows={4} maxLength={500} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal transition-colors resize-none" placeholder="第1章：準備・道具の確認&#10;第2章：基本的なしみ抜き手順&#10;..." />
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-stone-100">
                  <h3 className="font-sans text-sm font-medium text-charcoal mb-3">公開設定</h3>
                  <div className="flex gap-3">
                    {[{ v: "public", l: "公開", icon: "ri-earth-line" }, { v: "draft", l: "下書き", icon: "ri-draft-line" }].map((opt) => (
                      <button key={opt.v} onClick={() => setForm(f => ({ ...f, visibility: opt.v }))} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-sans border transition-colors cursor-pointer whitespace-nowrap ${form.visibility === opt.v ? "border-deep-green bg-deep-green/5 text-deep-green" : "border-stone-200 text-charcoal/50 hover:border-stone-300"}`}>
                        <i className={opt.icon} />{opt.l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6">
              <div className="flex gap-3">
                {step > 1 && (
                  <button onClick={() => setStep(s => s - 1)} className="px-5 py-2.5 rounded-full border border-stone-200 text-charcoal/60 text-sm font-sans cursor-pointer hover:border-stone-300 transition-colors whitespace-nowrap">
                    ← 戻る
                  </button>
                )}
                <button onClick={handleSave} className={`px-5 py-2.5 rounded-full border text-sm font-sans cursor-pointer transition-all whitespace-nowrap ${saved ? "border-teal text-teal" : "border-stone-200 text-charcoal/60 hover:border-stone-300"}`}>
                  {saved ? "✓ 保存済み" : "途中保存"}
                </button>
              </div>
              <button onClick={handleComplete} className="bg-deep-green text-white px-8 py-2.5 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">
                {step < 4 ? "次へ →" : "出品する →"}
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
